import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PiDeleteButton from "../../../components/PiDeleteButton";
import { CharacterPartType } from "../../../dto/CharacterPartDTO";
import { ICharacterPartDefinition } from "../../../models/interactions/SetCharacterParts";
import CharacterPartVariantDropdown from "./CharacterPartVariantDropdown";
import {
  selectCachedCharacterPartRenderByName,
  setCachedCharacterPartRender,
} from "../../../app/slices/CharacterPartsSlice";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import { CharacterPartService } from "../../../services/CharacterPartService";
import ClothesIcon from "../../../icons/ClothesIcon";
import { RootState } from "../../../app/Store";

export default function CharacterPartDefinitionPreview(props: {
  definition: ICharacterPartDefinition;
  onVariantSelected: (variantId: string) => void;
  onDelete: () => void;
}) {
  const { definition, onVariantSelected, onDelete } = props;
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const renderUri = useSelector((state: RootState) =>
    selectCachedCharacterPartRenderByName(state, definition.partId)
  );
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  useEffect(() => {
    if (!currentOrganization?.id || renderUri) return;

    const loadRender = async () => {
      const render = await CharacterPartService.GetCharacterPartGenderedPairRender(
        currentOrganization.id,
        definition.partId
      );
      if (!render?.downloadUri) return;
      dispatch(
        setCachedCharacterPartRender({
          characterPartName: definition.partId,
          downloadUri: render.downloadUri,
        })
      );
    };

    loadRender();
  }, [currentOrganization?.id, definition.partId, dispatch, renderUri]);

  return (
    <div className="flex flex-col w-full border-2 border-solid border-creator-border rounded-[5px]">
      {/* Square image area */}
      <div className="relative aspect-[2/1] w-full overflow-hidden rounded-t-[3px] bg-interaction-softBackground">
        {renderUri && !hasImageError ? (
          <>
            {!isImageLoaded && (
              <div className="absolute inset-0 animate-pulse bg-creator-surface" />
            )}
            <img
              className="absolute inset-0 h-full w-full object-contain p-[20px]"
              src={renderUri}
              alt={definition.partId}
              onLoad={() => setIsImageLoaded(true)}
              onError={() => {
                setHasImageError(true);
                setIsImageLoaded(false);
              }}
            />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-creator-text">
            <ClothesIcon size={40} />
          </div>
        )}
        <div className="absolute right-[4px] top-[4px]">
          <PiDeleteButton onClick={onDelete} size="small" />
        </div>
      </div>
      {/* Metadata + controls */}
      <div className="flex flex-col gap-[4px] p-[8px]">
        <div className="flex items-center gap-[6px] min-w-0">
          <span className="truncate font-bold text-[14px] text-interaction-primary">
            {definition.partId}
          </span>
          <span className="shrink-0 text-[12px] text-creator-text-sub">·</span>
          <span className="shrink-0 text-[12px] text-creator-text-sub">
            {CharacterPartType[definition.partType]}
          </span>
        </div>
        <div className="text-base font-bold text-interaction-primary h-[30px] mt-[8px]">Variant</div>
        <div className="h-[32px] overflow-hidden">
          <CharacterPartVariantDropdown
            definition={definition}
            onVariantSelected={onVariantSelected}
          />
        </div>
      </div>
    </div>
  );
}
